import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, StatusBadge } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Filter, Eye, Download } from 'lucide-react';

// How this client is engaged — the thing that decides the rate in the next column, and which
// was not shown anywhere until now. A partner could see WHAT they earn without being able to
// see WHY, so an attribution that had quietly defaulted to referral was invisible.
// A stored rate of 0 means two different things, and the screen used to say the wrong one.
// `affiliate` is priced as a flat fee and carries the sentinel 0; `reseller` earns NOTHING,
// because the margin was already taken when the partner bought the credits. Both rendered as
// "₹300 flat", so every reseller client on this table claimed a fee that is not paid.
// Only the arrangement can tell them apart, which is why this could not be right until the
// arrangement was on the row.
const formatCommissionRate = (client) => {
  const arrangement = client.arrangement || 'referral';
  if (arrangement === 'reseller') return '—';
  // A stored 0 means two different things. On an affiliate it is the silver flat fee; on a
  // client written before `arrangement` existed it just means nobody ever set a rate — and
  // those are paid the card's referral rate, not ₹300. Claiming a flat fee on a referral row
  // is the same false-number bug the reseller line above was added to stop, one branch down.
  if (!client.commission_rate) return arrangement === 'affiliate' ? '₹300 flat' : '—';
  return `${(client.commission_rate * 100).toFixed(0)}%`;
};

const ARRANGEMENT_LABEL = {
  referral: 'Referral',
  solution: 'Solution',
  affiliate: 'Affiliate',
  reseller: 'Reseller',
};

const ArrangementBadge = ({ client }) => {
  const current = client.arrangement || 'referral';
  const pending =
    client.arrangement_proposal_status === 'proposed' ? client.proposed_arrangement : null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="px-2 py-0.5 rounded bg-[#F4F4F1] text-[#1A1A1A] text-xs font-medium w-fit">
        {ARRANGEMENT_LABEL[current] || current}
      </span>
      {pending && (
        <span
          className="text-[11px] text-[#64748B]"
          title="Requested. The rate does not change until Expedify confirms it."
        >
          {ARRANGEMENT_LABEL[pending] || pending} requested
        </span>
      )}
    </div>
  );
};

const PartnerClients = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/partner/clients${params}`, {
        headers: getAuthHeaders()
      });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.business_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'all' || client.plan === planFilter;
    return matchesSearch && matchesPlan;
  });

  const exportToCSV = () => {
    const headers = ['Client Name', 'Business', 'Plan', 'Monthly Value', 'Promo Code', 'Referred On', 'Status', 'Commission Rate', 'Monthly Commission', 'Total Earned'];
    const rows = filteredClients.map(c => [
      c.client_name,
      c.business_name,
      c.plan,
      c.monthly_value,
      c.promo_code || '',
      formatDate(c.referred_on),
      c.status,
      formatCommissionRate(c),
      c.monthly_commission,
      c.total_earned,
    ]);
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-clients-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-clients">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              My Clients
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Track all your referred clients and their status
            </p>
          </div>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by client or business name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                  data-testid="search-clients"
                />
              </div>
              <div className="flex gap-3">
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-36 h-10 border-[#E2E2DF]">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 h-10 border-[#E2E2DF]" data-testid="filter-status">
                    <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">#</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client Name</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Business</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Plan</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Monthly Value</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Promo Code</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Referred On</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Engagement</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Commission Rate</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Monthly Commission</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Total Earned</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8 text-[#64748B]">
                      No clients found. Start referring to see them here!
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client, index) => (
                    <TableRow key={client.id} className="hover:bg-[#FAFAF8]/50" data-testid={`client-row-${index}`}>
                      <TableCell className="text-sm text-[#64748B]">{index + 1}</TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">{client.client_name}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{client.business_name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-[#FAFAF8] text-[#1A1A1A] capitalize">
                          {client.plan}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">{formatCurrency(client.monthly_value)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {client.promo_code ? (
                          <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
                            {client.promo_code} ({client.promo_discount}% off)
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatDate(client.referred_on)}</TableCell>
                      <TableCell>
                        <StatusBadge status={client.status} />
                      </TableCell>
                      <TableCell>
                        <ArrangementBadge client={client} />
                      </TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">
                        {formatCommissionRate(client)}
                      </TableCell>
                      <TableCell className="font-medium text-green-600">{formatCurrency(client.monthly_commission)}</TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">{formatCurrency(client.total_earned)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedClient(client)}
                          data-testid={`view-client-${index}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Client Detail Modal */}
        <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Client Details</DialogTitle>
            </DialogHeader>
            {selectedClient && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Client Name</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedClient.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Business Name</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedClient.business_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Business Type</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedClient.business_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Plan</p>
                    <p className="font-medium text-[#1A1A1A] capitalize">{selectedClient.plan}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Monthly Value</p>
                    <p className="font-medium text-[#1A1A1A]">{formatCurrency(selectedClient.monthly_value)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Status</p>
                    <StatusBadge status={selectedClient.status} />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Referred On</p>
                    <p className="font-medium text-[#1A1A1A]">{formatDate(selectedClient.referred_on)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Conversion Date</p>
                    <p className="font-medium text-[#1A1A1A]">{formatDate(selectedClient.conversion_date)}</p>
                  </div>
                </div>
                {selectedClient.promo_code && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 uppercase tracking-wider font-semibold">Promo Code Applied</p>
                        <p className="font-bold text-purple-700 text-lg">{selectedClient.promo_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-purple-600">Discount</p>
                        <p className="font-bold text-purple-700">{selectedClient.promo_discount}% off</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-[#E2E2DF]">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-[#FAFAF8] rounded-lg">
                      <p className="text-xs text-[#64748B]">Commission Rate</p>
                      <p className="text-lg font-bold text-[#ef785b]">
                        {formatCommissionRate(selectedClient)}
                      </p>
                    </div>
                    <div className="p-3 bg-[#FAFAF8] rounded-lg">
                      <p className="text-xs text-[#64748B]">Monthly</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(selectedClient.monthly_commission)}</p>
                    </div>
                    <div className="p-3 bg-[#FAFAF8] rounded-lg">
                      <p className="text-xs text-[#64748B]">Total Earned</p>
                      <p className="text-lg font-bold text-[#1A1A1A]">{formatCurrency(selectedClient.total_earned)}</p>
                    </div>
                  </div>
                </div>
                {selectedClient.notes && (
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Notes</p>
                    <p className="text-sm text-[#1A1A1A]">{selectedClient.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PartnerClients;
